/* eslint-disable @typescript-eslint/prefer-for-of */
import i18n from '@i18next/i18n';
import type { ISectionProperty } from './section.interface';
import { transaction } from '@src/slide/domain/slide-app';
import {
    IPresentationBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/presentation-model.interface';
import { ISectionModel } from '@src/slide/domain/ui-model/element-modeles/Interface/section-model.interface';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import ClearanceUtils from '@src/slide/domain/ui-model/utils/clearance-utils';
import { IPresentationDataModel } from '@tencent/slide-sdk-model';

export { ISectionProperty } from './section.interface';

const DEFAULT_SECTION_NAME = i18n.t('默认节');

export default class SectionProperty implements ISectionProperty {
    // eslint-disable-next-line no-useless-constructor
    public constructor(private presentation: IPresentationBaseModel) {}

    public getSectionByIndex(sectionIndex: number): ISectionModel | undefined {
        if (!this.presentation.sectionList
            || sectionIndex < 0
            || sectionIndex >= this.presentation.sectionList.length
        ) {
            return undefined;
        }
        return this.presentation.sectionList[sectionIndex];
    }

    public addNewSection(clearanceIndex: number, name: string): void {
        if (!this.presentation.selectionService) {
            return;
        }
        this.presentation.selectionService.slideClearanceIndex = -1;
        const presentationSdk = this.presentation.sdkData as IPresentationDataModel;
        presentationSdk.api.transaction(() => {
            if ((this.presentation.sectionList?.length ?? 0) === 0 && clearanceIndex !== 0) {
                presentationSdk.api.addSection(DEFAULT_SECTION_NAME, 0);
            }
            presentationSdk.api.addSection(name, clearanceIndex);
        });
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.SECTION,
            action: TDWActionType.ADD_SECTION,
        });
    }

    public renameSection(sectionIndex: number, newName: string): void {
        const { sectionList } = this.presentation;
        if (!sectionList || sectionIndex < 0 || sectionList.length <= sectionIndex) {
            window.log?.error('SectionProperty renameSection Error! invalid sectionIndex: ', sectionIndex, sectionList);
            return;
        }
        sectionList[sectionIndex].updateProps({ name: newName });
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.SECTION,
            action: TDWActionType.MODIFY_SECTION,
        });
    }

    public deleteSection(sectionIndex: number, shouldAlsoDeleteSlides?: boolean): void {
        const { sectionList } = this.presentation;
        if (!sectionList) {
            return;
        }
        if (sectionIndex < 0 || sectionList.length <= sectionIndex) {
            window.log?.error('SectionProperty deleteSection Error! invalid sectionIndex: ', sectionIndex, sectionList);
            return;
        }
        const presentationSdk = this.presentation.sdkData as IPresentationDataModel;
        presentationSdk.api.transaction(() => {
            if (shouldAlsoDeleteSlides) {
                // 传这个参数的时候默认已经选中了所有对应的 slides
                this.presentation.deleteSlide();
            }
            presentationSdk.api.removeSections(presentationSdk.sectionList.slice(sectionIndex, sectionIndex + 1));
        });
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.SECTION,
            action: TDWActionType.DELETE_SECTION,
        });
    }

    public deleteAllSections(): void {
        this.presentation.sectionList = undefined;
        const presentationSdk = this.presentation.sdkData as IPresentationDataModel;
        presentationSdk.api.removeSections(presentationSdk.sectionList);
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.SECTION,
            action: TDWActionType.DELETE_SECTION,
        });
    }

    public countSection(sectionIndex: number): number {
        const { sectionList } = this.presentation;
        if (!sectionList || sectionIndex < 0 || sectionIndex >= sectionList.length) {
            return 0;
        }
        return sectionList[sectionIndex].numSlides;
    }

    public moveSectionUp(sectionIndex: number): void {
        this.moveSection(sectionIndex, sectionIndex - 1);
    }

    public moveSectionDown(sectionIndex: number): void {
        // 3.0的toIndex是间隙索引，下移时目标索引是节索引+2
        this.moveSection(sectionIndex, sectionIndex + 2);
    }

    public moveSection(from: number, to: number): void {
        const { sectionList } = this.presentation;
        if (!sectionList?.length) {
            return;
        }
        if (((from + 1) === to) || (from === to)) {
            // 目标当前节上下的间隙都是未移动
            return;
        }
        const presentationSdk = this.presentation.sdkData as IPresentationDataModel;
        presentationSdk.api.transaction(() => {
            presentationSdk.api.moveSection(presentationSdk.sectionList[from], to);
        });
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.SECTION,
            action: TDWActionType.MOVE_SECTION,
        });
    }

    public getSectionSlideModelsAndBeginSlideIndex(sectionIndex: number): [ISlideModel[], number] {
        const { sectionList } = this.presentation;
        if (!sectionList || sectionIndex < 0 || sectionIndex >= sectionList.length) {
            return [[], -1];
        }
        let beginIndex = 0;
        for (let i = 0; i < sectionIndex; i += 1) {
            beginIndex += sectionList[i].numSlides;
        }
        const slideModels = this.presentation.children.slice(
            beginIndex,
            beginIndex + sectionList[sectionIndex].numSlides,
        );
        return [slideModels, beginIndex];
    }

    public insertSlideIntoCorrespondingSection(clearanceIndex: number, insertNum = 1): void {
        const { sectionList } = this.presentation;
        if (!sectionList || sectionList.length === 0) {
            return;
        }
        let prevSectionCount = 0;
        let index = 0;
        while (
            prevSectionCount < sectionList.length
            && clearanceIndex > sectionList[prevSectionCount].numSlides + index
        ) {
            index += sectionList[prevSectionCount].numSlides + 1;
            prevSectionCount += 1;
        }
        const sectionIndex = Math.min(prevSectionCount, sectionList.length - 1);
        sectionList[sectionIndex].updateProps({ numSlides: sectionList[sectionIndex].numSlides + insertNum });
    }

    public deleteSlideFromSectionList(slideIds: string[]): void {
        const { sectionList } = this.presentation;
        if (!sectionList || sectionList.length === 0) {
            return;
        }
        let slideIdIndex = 0;
        const slideIndices: number[] = [];
        for (let i = 0; i < this.presentation.children.length; i += 1) {
            if (this.presentation.children[i].id === slideIds[slideIdIndex]) {
                slideIndices.push(i);
                slideIdIndex += 1;
                i = -1;
            }
        }
        const numSlidesDeltas = sectionList.map(() => 0);
        slideIndices.forEach((index) => {
            const sectionIndex = ClearanceUtils.calcSectionIndexFromSlideIndex(index, sectionList);
            numSlidesDeltas[sectionIndex] += 1;
        });
        transaction(() => {
            numSlidesDeltas.forEach((delta, index) => {
                if (delta === 0 || !sectionList[index]) {
                    return;
                }
                const { numSlides } = sectionList[index];
                sectionList[index].updateProps({ numSlides: numSlides - delta });
            });
        });
    }

    public adjustSectionSizesOnMovingSlides(fromIndices: number[], toSectionIndex: number): void {
        if (fromIndices.length === 0 || toSectionIndex < 0) {
            return;
        }
        const { sectionList } = this.presentation;
        if (!sectionList || sectionList.length === 0) {
            return;
        }
        const lengthDeltas = sectionList.map(() => 0);
        fromIndices.forEach((fromIndex) => {
            const fromSectionIndex = ClearanceUtils.calcSectionIndexFromSlideIndex(fromIndex, sectionList);
            lengthDeltas[fromSectionIndex] += 1;
        });
        transaction(() => {
            lengthDeltas.forEach((delta, index) => {
                if (index === toSectionIndex) {
                    sectionList[index].updateProps({
                        numSlides: sectionList[index].numSlides + fromIndices.length - delta,
                    });
                } else if (delta !== 0) {
                    sectionList[index].updateProps({
                        numSlides: sectionList[index].numSlides - delta,
                    });
                }
            });
        });
    }
}
